import { useCallback } from 'react'
import { useLanguage } from '@/lib/i18n/language-provider'
import { toast } from 'sonner'
import type { WorkspaceElement } from '@/types/synoptique'

interface ElementGroup {
  name: string
  elementIds: string[]
  permanent?: boolean
  isTemporarilyUngrouped?: boolean
}

interface UseGroupManagementParams {
  elementGroups: Record<string, ElementGroup>
  setElementGroups: (fn: (prev: Record<string, ElementGroup>) => Record<string, ElementGroup>) => void
  workspaceElements: WorkspaceElement[]
  selectedElements: string[]
}

export function useGroupManagement({
  elementGroups,
  setElementGroups,
  workspaceElements,
  selectedElements,
}: UseGroupManagementParams) {
  const { t } = useLanguage()

  const getGroupForElement = useCallback((elementId: string): string | null => {
    for (const [groupId, group] of Object.entries(elementGroups)) {
      if (group.elementIds.includes(elementId)) {
        if (group.isTemporarilyUngrouped) {
          return null
        }
        return groupId
      }
    }
    return null
  }, [elementGroups])

  const isElementInGroup = useCallback((elementId: string): boolean => {
    return getGroupForElement(elementId) !== null
  }, [getGroupForElement])

  const createGroup = useCallback((elementIds: string[]) => {
    if (elementIds.length < 2) return

    const existingGroups = new Set<string>()
    const ungroupedElements: string[] = []

    elementIds.forEach(elementId => {
      const groupId = getGroupForElement(elementId)
      if (groupId) {
        existingGroups.add(groupId)
      } else {
        ungroupedElements.push(elementId)
      }
    })

    const existingGroupsArray = Array.from(existingGroups)

    if (existingGroupsArray.length === 0) {
      const groupId = `group-${Date.now()}`
      const groupName = `Groupe ${Object.keys(elementGroups).length + 1}`

      setElementGroups(prev => ({
        ...prev,
        [groupId]: {
          name: groupName,
          elementIds: [...elementIds]
        }
      }))
    } else if (existingGroupsArray.length === 1) {
      const existingGroupId = existingGroupsArray[0]

      setElementGroups(prev => {
        const existingGroup = prev[existingGroupId]
        const allElementIds = [...new Set([...existingGroup.elementIds, ...ungroupedElements])]

        return {
          ...prev,
          [existingGroupId]: {
            ...existingGroup,
            elementIds: allElementIds
          }
        }
      })
    } else {
      const firstGroupId = existingGroupsArray[0]
      const allElementIds = new Set<string>()

      existingGroupsArray.forEach(groupId => {
        const group = elementGroups[groupId]
        group.elementIds.forEach(id => allElementIds.add(id))
      })

      ungroupedElements.forEach(id => allElementIds.add(id))

      setElementGroups(prev => {
        const newGroups = { ...prev }

        newGroups[firstGroupId] = {
          ...prev[firstGroupId],
          elementIds: Array.from(allElementIds)
        }

        existingGroupsArray.slice(1).forEach(groupId => {
          delete newGroups[groupId]
        })

        return newGroups
      })
    }
  }, [elementGroups, setElementGroups, getGroupForElement])

  const deleteGroup = useCallback((groupId: string) => {
    const group = elementGroups[groupId]
    if (group?.permanent) {
      toast.error(t('synoptic.toast.groupLocked'), {
        description: t('synoptic.toast.groupLockedDesc')
      })
      return
    }

    setElementGroups(prev => {
      const newGroups = { ...prev }
      delete newGroups[groupId]
      return newGroups
    })
  }, [elementGroups, setElementGroups, t])

  const getEffectiveSelectionCount = useCallback((): number => {
    const accountedElements = new Set<string>()
    let count = 0

    selectedElements.forEach(elementId => {
      if (accountedElements.has(elementId)) return

      const element = workspaceElements.find(el => el.id === elementId)
      if (element?.groupId) {
        const group = elementGroups[element.groupId]
        if (group?.permanent && !group.isTemporarilyUngrouped) {
          const allGroupElementsSelected = group.elementIds.every(id => selectedElements.includes(id))
          if (allGroupElementsSelected) {
            count++
            group.elementIds.forEach(id => accountedElements.add(id))
            return
          }
        }
      }

      count++
      accountedElements.add(elementId)
    })

    return count
  }, [selectedElements, workspaceElements, elementGroups])

  const getGroupBounds = useCallback((groupId: string): { x: number, y: number, width: number, height: number } | null => {
    const group = elementGroups[groupId]
    if (!group) return null

    const elements = workspaceElements.filter(el => group.elementIds.includes(el.id))
    if (elements.length === 0) return null

    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity

    elements.forEach(el => {
      minX = Math.min(minX, el.position.x)
      minY = Math.min(minY, el.position.y)
      maxX = Math.max(maxX, el.position.x + el.size.width)
      maxY = Math.max(maxY, el.position.y + el.size.height)
    })

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY
    }
  }, [elementGroups, workspaceElements])

  return {
    createGroup,
    deleteGroup,
    getGroupForElement,
    isElementInGroup,
    getEffectiveSelectionCount,
    getGroupBounds,
  }
}
